// Material UI imports
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
// Angular imports
import { Component, inject, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
// Utility imports
import { UtilsService } from 'src/app/shared/utils/utils.service';
// Domain imports
import { IPatientRequestDTO, IPatientResponseDTO } from 'src/app/core/domain/patient';
import { IMutation, GenomicCoordinate } from 'src/app/core/domain/mutation';
import {
  PatientLifecycleState,
  getLifecycleState,
  getLifecycleStateVariant,
  canEdit,
  canDelete,
  canRequestCorrection,
  getEditDisabledReason,
  getDeleteDisabledReason
} from 'src/app/core/domain/patient-lifecycle';
import { MasterTablesService, MasterDataOption, Country } from 'src/app/core/services/master-tables.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { AppInsightsService } from 'src/app/core/services/app-insights.service';
import { DiagnosisTabComponent } from './tabs/diagnosis/diagnosis-tab.component';
import { ConfirmDialogComponent, ConfirmDialogData, ConfirmDialogResult } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { TrimOnBlurDirective } from 'src/app/shared/directives/trim-on-blur.directive';

/** Display-ready fields for a single confirmed mutation. */
export interface MutationView {
  coding: string;
  protein: string | null;
  genomic: string | null;
  variantType: string | null;
  transcript: string | null;
  /** 1-based position, used for the aria/group label fallback. */
  index: number;
}

/**
 * Patient Home — unified "passport" surface.
 *
 * Replaces the former two-tab (Info / Diagnosis) layout with a single
 * scrollable page that walks the caregiver through the patient lifecycle:
 * onboarding (with a progress bar and the genetic-report upload as the hero
 * action) flowing into the confirmed passport. Edit access follows the
 * backend `capabilities` enrichment, so the profile locks automatically once
 * the diagnosis is confirmed.
 */
@Component({
  selector: 'patient-item-list',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatInputModule,
    MatNativeDateModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    DiagnosisTabComponent,
    TrimOnBlurDirective
  ],
  providers: [DatePipe],
  templateUrl: './patient-item-list.component.html',
  styleUrls: [
    './patient-item-list.component.scss',
    './patient-item-list.component.mutations.scss'
  ]
})
export class PatientItemListComponent implements OnInit, OnDestroy {
  @Input() patient: IPatientResponseDTO;
  @Output() patientDeleted = new EventEmitter<string>();

  form: FormGroup;

  // Current app language, mirrored from ngx-translate so the birth-date pipe
  // renders in the active locale (matches DiagnosisTabComponent). Initialized
  // in ngOnInit because the `translate` injection is declared further below.
  currentLang = 'en';
  private langChangeSub?: Subscription;

  // Responsive flag
  isMobile = false;

  // Datepicker bounds
  maxDate: Date;
  minDate: Date;
  startDate: Date;

  // Edit mode
  isEditing = false;

  // Collapsible "personal information" section
  personalInfoOpen = true;
  // Collapsible "confirmed mutation" details card
  mutationDetailsOpen = true;
  // Collapsible RettXID explanation
  rettxidInfoOpen = false;

  // Driven by the embedded diagnosis card: true while a genetic report is still needed
  needsGeneticsUpload = false;

  // Master data (resolved once for both the edit selects and the read-only display)
  genders: MasterDataOption[] = [];
  relationships: MasterDataOption[] = [];
  countries: Country[] = [];

  private fb = inject(FormBuilder);
  private patientService = inject(PatientService);
  public masterTablesService = inject(MasterTablesService);
  public utilService = inject(UtilsService);
  private snackBar = inject(MatSnackBar);
  private appInsightsService = inject(AppInsightsService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private translate = inject(TranslateService);

  constructor(private breakpointObserver: BreakpointObserver) {}

  ngOnInit(): void {
    this.masterTablesService.getGenders().subscribe(list => (this.genders = list ?? []));
    this.masterTablesService.getRelationships().subscribe(list => (this.relationships = list ?? []));
    this.masterTablesService.getCountries().subscribe(list => (this.countries = list ?? []));

    const today = new Date();
    this.maxDate = today;
    this.startDate = today;
    this.minDate = new Date(1960, 0, 1);

    this.form = this.fb.group({
      given_name: [this.patient.given_name, Validators.required],
      family_name: [this.patient.family_name, Validators.required],
      date_of_birth: [this.utilService.parseStringToDatepickerDate(this.patient.date_of_birth), Validators.required],
      country_of_birth: [this.patient.country_of_birth, Validators.required],
      gender: [this.patient.gender, Validators.required],
      deceased: [false],
      deceased_date: [null],
      relationship: [this.patient.relationship, Validators.required]
    });

    this.isEditing = false;
    this.form.disable();

    // Personal information is expanded by default so families can review and
    // edit it at a glance; the genetic-report action still leads the page.
    this.personalInfoOpen = true;

    this.breakpointObserver.observe([Breakpoints.Handset]).subscribe(result => {
      this.isMobile = result.matches;
    });

    // Keep the birth-date locale in sync with the active + runtime language.
    this.currentLang = this.translate.currentLang || 'en';
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.currentLang = event.lang || 'en';
    });
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  // ===================== Lifecycle-derived view state =====================

  get lifecycleState(): PatientLifecycleState {
    return getLifecycleState(this.patient);
  }

  get validationStatusCode(): string | undefined {
    return this.patient?.mutation_summary?.validation?.status_code;
  }

  get isOnboarding(): boolean {
    const s = this.lifecycleState;
    return s === PatientLifecycleState.DRAFT || s === PatientLifecycleState.MORE_INFO_REQUIRED;
  }

  get isUnderReview(): boolean {
    return this.lifecycleState === PatientLifecycleState.UNDER_REVIEW;
  }

  get isConfirmed(): boolean {
    const s = this.lifecycleState;
    return s === PatientLifecycleState.CONFIRMED || s === PatientLifecycleState.LOCKED;
  }

  /** Progress strip is shown for every state that isn't fully confirmed. */
  get showProgress(): boolean {
    return !this.isConfirmed;
  }

  get progressPct(): number {
    if (this.isConfirmed) return 100;
    if (this.isUnderReview) return 90;
    return 50;
  }

  get progressVariant(): 'action' | 'review' | 'done' {
    if (this.isConfirmed) return 'done';
    if (this.isUnderReview) return 'review';
    return 'action';
  }

  get progressHintKey(): string {
    if (this.isConfirmed) return 'PATIENT_DETAIL.HOME.PROGRESS_HINT_DONE';
    if (this.isUnderReview) return 'PATIENT_DETAIL.HOME.PROGRESS_HINT_REVIEW';
    return 'PATIENT_DETAIL.HOME.PROGRESS_HINT_UPLOAD';
  }

  get statusKey(): string {
    return `PATIENT.LIFECYCLE.STATUS.${this.lifecycleState}`;
  }

  get statusVariant(): 'success' | 'warning' | 'info' | 'default' {
    return getLifecycleStateVariant(this.lifecycleState);
  }

  // ===================== Identity / demographics display =====================

  get initials(): string {
    const g = this.patient?.given_name?.trim()?.charAt(0) ?? '';
    const f = this.patient?.family_name?.trim()?.charAt(0) ?? '';
    return (g + f).toUpperCase() || '–';
  }

  get age(): number | null {
    const raw = this.patient?.date_of_birth;
    if (!raw) return null;
    const dob = new Date(raw);
    if (isNaN(dob.getTime())) return null;
    const now = new Date();
    let years = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) years--;
    return years >= 0 ? years : null;
  }

  /** Whole months of age — only meaningful for infants under one year. */
  get ageMonths(): number {
    const raw = this.patient?.date_of_birth;
    if (!raw) return 0;
    const dob = new Date(raw);
    if (isNaN(dob.getTime())) return 0;
    const now = new Date();
    let months = (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth());
    if (now.getDate() < dob.getDate()) months--;
    return months >= 0 ? months : 0;
  }

  getGenderName(): string {
    return this.genders.find(g => g.id === this.patient?.gender)?.name ?? this.patient?.gender ?? '';
  }

  getCountryName(): string {
    return this.countries.find(c => c.iso_code === this.patient?.country_of_birth)?.name ?? this.patient?.country_of_birth ?? '';
  }

  getRelationshipName(): string {
    return this.relationships.find(r => r.id === this.patient?.relationship)?.name ?? this.patient?.relationship ?? '';
  }

  // ===================== Mutation summary (concise) =====================

  get primaryMutation(): IMutation | null {
    return this.patient?.mutations?.length ? this.patient.mutations[0] : null;
  }

  /**
   * Per-mutation view-models for the confirmed card. A patient can carry more
   * than one validated mutation; the card renders one labelled group per entry.
   * Entries with no coding change (incomplete data) are dropped so the card
   * never shows an empty group.
   */
  get mutationViews(): MutationView[] {
    const list = this.patient?.mutations ?? [];
    return list
      .map((m, i) => this.buildMutationView(m, i))
      .filter((v): v is MutationView => v !== null);
  }

  /** Number of renderable mutations (those with a coding change). */
  get mutationCount(): number {
    return this.mutationViews.length;
  }

  /** True when the patient carries more than one renderable mutation. */
  get isMultiMutation(): boolean {
    return this.mutationCount > 1;
  }

  /** Card title adapts to one vs. many findings. */
  get mutationDetailsTitleKey(): string {
    return this.isMultiMutation
      ? 'PATIENT_DETAIL.HOME.MUTATION_DETAILS_TITLE_PLURAL'
      : 'PATIENT_DETAIL.HOME.MUTATION_DETAILS_TITLE';
  }

  /** Strip an HGVS prefix ("NM_..:c.473C>T" -> "c.473C>T"). */
  private stripHgvsPrefix(hgvs?: string | null): string | null {
    if (!hgvs) return null;
    const idx = hgvs.lastIndexOf(':');
    return idx >= 0 ? hgvs.slice(idx + 1) : hgvs;
  }

  /** Build a single mutation's display fields. Returns null when no coding change. */
  private buildMutationView(m: IMutation, index: number): MutationView | null {
    const data = m?.mutation_data;
    const coding = this.stripHgvsPrefix(data?.primary_transcript?.hgvs_transcript_variant);
    if (!coding) return null;

    const rawProtein = data?.primary_transcript?.protein_consequence_slr
      || data?.primary_transcript?.protein_consequence_tlr;
    const protein = !rawProtein || rawProtein.includes('?') ? null : this.stripHgvsPrefix(rawProtein);

    // genomic_coordinates may arrive as an array OR a keyed object depending on API version.
    const coords = data?.genomic_coordinates as unknown;
    let match: GenomicCoordinate | undefined;
    if (Array.isArray(coords)) {
      match = coords.find((c) => c?.assembly === 'GRCh38');
    } else if (coords && typeof coords === 'object') {
      const dict = coords as Record<string, GenomicCoordinate>;
      match = Object.values(dict).find((c) => c?.assembly === 'GRCh38') ?? dict['GRCh38'];
    }
    let genomic = match?.hgvs ? this.stripHgvsPrefix(match.hgvs) : null;
    if (!genomic && data?.genome_assembly === 'GRCh38' && data?.genomic_coordinate) {
      genomic = this.stripHgvsPrefix(data.genomic_coordinate);
    }

    const fullTranscript = data?.primary_transcript?.hgvs_transcript_variant;
    const tIdx = fullTranscript ? fullTranscript.indexOf(':') : -1;
    const transcript = tIdx > 0 ? fullTranscript!.slice(0, tIdx) : null;

    return {
      coding,
      protein,
      genomic,
      variantType: data?.variant_type || null,
      transcript,
      index: index + 1
    };
  }

  /** Coding-level change only, e.g. "c.473C>T" (no transcript prefix). */
  get mutationCoding(): string | null {
    return this.primaryMutation ? this.buildMutationView(this.primaryMutation, 0)?.coding ?? null : null;
  }

  /** Short protein consequence only, e.g. "p.(Arg306Cys)". */
  get mutationProtein(): string | null {
    return this.primaryMutation ? this.buildMutationView(this.primaryMutation, 0)?.protein ?? null : null;
  }

  get hasMutation(): boolean {
    return !!this.mutationCoding;
  }

  /** Genomic coordinate on GRCh38 only, prefix stripped, e.g. "g.154030912G>A". */
  get mutationGenomic(): string | null {
    return this.primaryMutation ? this.buildMutationView(this.primaryMutation, 0)?.genomic ?? null : null;
  }

  /** Variant type, e.g. "SNV". */
  get mutationVariantType(): string | null {
    return this.primaryMutation ? this.buildMutationView(this.primaryMutation, 0)?.variantType ?? null : null;
  }

  /** Primary transcript accession only, e.g. "NM_004992.4" (no coding suffix). */
  get mutationTranscript(): string | null {
    return this.primaryMutation ? this.buildMutationView(this.primaryMutation, 0)?.transcript ?? null : null;
  }

  /**
   * Pending-mutation label that stays coherent with the lifecycle: only ask for
   * the genetic report while the family still owns that step. Once the report is
   * uploaded (under review or beyond) the mutation is simply being determined.
   */
  get mutationPendingKey(): string {
    return this.isOnboarding
      ? 'PATIENT_DETAIL.HOME.MUTATION_PENDING'
      : 'PATIENT_DETAIL.HOME.MUTATION_REVIEW';
  }

  // ===================== Capabilities =====================

  get canEditPatient(): boolean {
    return canEdit(this.patient);
  }

  get canDeletePatient(): boolean {
    return canDelete(this.patient);
  }

  get canRequestCorrectionPatient(): boolean {
    return canRequestCorrection(this.patient);
  }

  getEditReasonKey(): string {
    return getEditDisabledReason(this.patient);
  }

  getDeleteReasonKey(): string {
    return getDeleteDisabledReason(this.patient);
  }

  // ===================== Section toggles =====================

  togglePersonalInfo(): void {
    this.personalInfoOpen = !this.personalInfoOpen;
  }

  toggleMutationDetails(): void {
    this.mutationDetailsOpen = !this.mutationDetailsOpen;
  }

  toggleRettxidInfo(): void {
    this.rettxidInfoOpen = !this.rettxidInfoOpen;
  }

  // ===================== Edit / save / delete =====================

  onEditClick(): void {
    if (!this.canEditPatient) {
      const reason = this.translate.instant(this.getEditReasonKey());
      this.snackBar.open(reason, this.translate.instant('COMMON.CLOSE'), {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
      return;
    }
    // Make sure the section is open so the form is visible while editing
    this.personalInfoOpen = true;
    this.isEditing = true;
    this.form.enable();
  }

  disableEditing(): void {
    this.isEditing = false;
    this.form.disable();
  }

  saveInfoPatient(): void {
    if (!this.form.valid) return;

    const dataFormPatient: IPatientRequestDTO = this.form.getRawValue();
    const dob: Date = this.form.value.date_of_birth;
    dataFormPatient.date_of_birth = dob ? this.utilService.formatDateToString(dob) : null;
    dataFormPatient.given_name = dataFormPatient.given_name.trim();
    dataFormPatient.family_name = dataFormPatient.family_name.trim();
    dataFormPatient.name = `${dataFormPatient.given_name} ${dataFormPatient.family_name}`;
    dataFormPatient.deceased_date = null;

    this.patientService.savePatient(this.patient.rettxid, dataFormPatient).subscribe({
      next: () => {
        // Reflect saved values locally so the passport updates without a full reload
        Object.assign(this.patient, {
          given_name: dataFormPatient.given_name,
          family_name: dataFormPatient.family_name,
          name: dataFormPatient.name,
          date_of_birth: dataFormPatient.date_of_birth,
          country_of_birth: dataFormPatient.country_of_birth,
          gender: dataFormPatient.gender,
          relationship: dataFormPatient.relationship
        });
        this.disableEditing();
      },
      error: () => {}
    });
  }

  onDeleteClick(): void {
    if (!this.canDeletePatient) {
      const reason = this.translate.instant(this.getDeleteReasonKey());
      this.snackBar.open(reason, this.translate.instant('COMMON.CLOSE'), {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
      return;
    }

    const messageKey = this.lifecycleState === PatientLifecycleState.DRAFT
      ? 'PATIENT.LIFECYCLE.CONFIRM_DELETE.MESSAGE_DRAFT'
      : 'PATIENT.LIFECYCLE.CONFIRM_DELETE.MESSAGE_MORE_INFO';

    const dialogData: ConfirmDialogData = {
      titleKey: 'PATIENT.LIFECYCLE.CONFIRM_DELETE.TITLE',
      messageKey,
      confirmKey: 'PATIENT.LIFECYCLE.CONFIRM_DELETE.CONFIRM',
      cancelKey: 'PATIENT.LIFECYCLE.CONFIRM_DELETE.CANCEL',
      type: 'danger',
      icon: 'delete'
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
      width: '400px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: ConfirmDialogResult | undefined) => {
      if (result?.confirmed) {
        this.deletePatient();
      }
    });
  }

  private deletePatient(): void {
    this.patientService.deletePatient(this.patient.rettxid).subscribe({
      next: () => {
        this.appInsightsService.trackTrace('Patient deleted', {
          rettxid: this.patient.rettxid,
          lifecycleState: this.lifecycleState
        });
        this.patientDeleted.emit(this.patient.rettxid);
        this.router.navigate(['/patients']);
      },
      error: (err) => {
        this.appInsightsService.trackException(err);
      }
    });
  }

  openCorrectionInfo(): void {
    const dialogData: ConfirmDialogData = {
      titleKey: 'PATIENT.LIFECYCLE.CORRECTION.INFO_TITLE',
      messageKey: 'PATIENT.LIFECYCLE.CORRECTION.INFO_MESSAGE',
      messageParams: { rettxid: this.patient.rettxid },
      confirmKey: 'COMMON.OK',
      type: 'info',
      icon: 'email',
      showCancelButton: false
    };

    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
      width: '450px',
      maxWidth: '95vw'
    });
  }

  // ===================== Diagnosis card wiring =====================

  /**
   * Refreshes patient data from the backend after a file upload so the
   * passport (lifecycle, capabilities, mutation summary) reflects the new state.
   */
  refreshPatientData(): void {
    this.patientService.getPatient(this.patient.rettxid).subscribe({
      next: (updatedPatient: IPatientResponseDTO) => {
        Object.assign(this.patient, updatedPatient);
      },
      error: (err) => {
        console.error('Error refreshing patient data:', err);
      }
    });
  }

  // ===================== RettXID =====================

  copyRettxid(): void {
    const id = this.patient?.rettxid;
    if (!id || !navigator.clipboard) {
      this.onRettxidCopied(false);
      return;
    }
    navigator.clipboard.writeText(id)
      .then(() => this.onRettxidCopied(true))
      .catch(() => this.onRettxidCopied(false));
  }

  private onRettxidCopied(success: boolean): void {
    if (success) {
      this.snackBar.open(
        this.translate.instant('PATIENT_DETAIL.CONTENT.RETTXID.COPIED'),
        this.translate.instant('COMMON.CLOSE'),
        { duration: 3000, horizontalPosition: 'center', verticalPosition: 'bottom', panelClass: ['snackbar-success'] }
      );
      this.appInsightsService.trackTrace('RettXID copied from patient block', {
        rettxid: this.patient.rettxid,
        location: 'patient-passport'
      });
    } else {
      this.snackBar.open(
        this.translate.instant('PATIENT_DETAIL.CONTENT.RETTXID.COPY_FAILED'),
        this.translate.instant('COMMON.CLOSE'),
        { duration: 5000, horizontalPosition: 'center', verticalPosition: 'bottom', panelClass: ['snackbar-error'] }
      );
      this.appInsightsService.trackException(new Error('Failed to copy RettXID from patient block'));
    }
  }
}
